// Jitsi Meet helper — generates unguessable room URLs for in-app calling.
// Uses the OFFICIAL meet.jit.si instance — guaranteed-to-be-up, run by the Jitsi
// project itself. The trade-off: the FIRST person to join the room (the "moderator")
// is asked to sign in with Google/GitHub/Facebook ONCE per browser (session is
// remembered thereafter). All other participants join without any login.
// We previously tried meet.calyx.net (DNS went dead) and meet.ffmuc.net (had a
// "MANUAL / FAQ / CONTACT" community overlay) — neither suitable for client calls.
// No SDK key, no infra. Room is created on first visit.
// Format: https://meet.jit.si/<prefix>-<8-char-random>
//
// Modes (apply Jitsi config via URL fragment):
//   video        — default video + mic on
//   voice        — audio-only (camera off, audio-only flag on)
//   screen       — opens with screen-sharing prompt
//
// References:
//   https://jitsi.org/meet/  (official instance)
//   https://github.com/jitsi/jitsi-meet/blob/master/config.js  (config flags)

const JITSI_BASE = 'https://meet.jit.si';

// 8 unguessable URL-safe chars (~47 bits — plenty for ad-hoc rooms)
const randomSuffix = (len = 8) => {
  const alphabet = 'abcdefghjkmnpqrstuvwxyz23456789'; // omit i/l/o/0/1 for readability
  const out = [];
  const bytes = new Uint8Array(len);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  for (let i = 0; i < len; i++) out.push(alphabet[bytes[i] % alphabet.length]);
  return out.join('');
};

const slug = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24) || 'room';

// Build the call URL. `prefix` should be short context like "chat", "appt", "dm".
// `contextId` is optional (e.g. chat-room id or appointment id) — included so each
// chat/appointment gets a stable but still randomised room name.
export function makeMeetingUrl({ prefix = 'call', contextId = '', mode = 'video' } = {}) {
  const ctx = contextId ? `-${slug(contextId).slice(0, 10)}` : '';
  const room = `dextra-${slug(prefix)}${ctx}-${randomSuffix(8)}`;
  let frag = '';
  if (mode === 'voice') {
    // Audio-only: video starts muted + audio-only mode on
    frag = '#config.startWithVideoMuted=true&config.startAudioOnly=true';
  } else if (mode === 'screen') {
    // Opens with screen-share picker
    frag = '#config.startScreenSharing=true';
  }
  return `${JITSI_BASE}/${room}${frag}`;
}

// Detect a call card in a chat message text. Returns { mode, url } or null.
// Call cards are plain-text messages that start with our marker emoji + "call" word
// and contain a Jitsi URL — so they remain readable in fallback clients (mobile, email).
// We accept any meet.* Jitsi URL so older messages with meet.jit.si links still render
// as call cards after we switched the base URL to meet.ffmuc.net.
export function parseCallCard(text) {
  if (!text || typeof text !== 'string') return null;
  const t = text.trim();
  // Match: <emoji> ...call... <https://meet.<jitsi-host>/...>
  const m = t.match(/^(📹|📞|🖥️)\s+.*(https:\/\/meet\.(?:ffmuc\.net|jit\.si|calyx\.net|element\.io)\/[^\s]+)/i);
  if (!m) return null;
  const emoji = m[1];
  const url = m[2];
  const mode = emoji === '📹' ? 'video' : emoji === '📞' ? 'voice' : 'screen';
  return { mode, url, emoji };
}

export const CALL_MODE_LABEL = {
  video: 'Video Call',
  voice: 'Voice Call',
  screen: 'Screen Share',
};

export const CALL_MODE_BG = {
  video: '#dcfce7',  // emerald-100
  voice: '#dbeafe',  // blue-100
  screen: '#fef3c7', // amber-100
};

export const CALL_MODE_FG = {
  video: '#166534',
  voice: '#1e40af',
  screen: '#92400e',
};
